import { AlertTriangle } from 'lucide-react';
import type { Appointment } from '../../types/appointments';
import { formatTimeInTimeZone, formatDateInTimeZone } from '../../utils/timezoneDisplay';

// ─────────────────────────────────────────────────────
//  ApprovalQueueCard — shared approval decision card
//  Used by: Dashboard, Schedule (AppointmentsTab)
// ─────────────────────────────────────────────────────

export interface ApprovalQueueCardProps {
    request: Appointment;
    /** Card body click (usually opens detail view) */
    onCardClick?: () => void;
    /** Approve action */
    onApprove: () => void;
    /** Revise Time action */
    onRevise: () => void;
    /** Decline action */
    onDecline: () => void;
    warehouseTimeZone?: string;
    /** Warehouse timezone for formatting */
    
}

// ── Late PO Risk helper ──────────────────────────────
export interface LatePORisk {
    severity: 'standard' | 'urgent' | 'critical';
    label: string;
    latePOs: { poNumber: string; dueDate: string; priority: string }[];
}

export function detectLatePORisk(request: Appointment): LatePORisk | null {
    const linkedPOs = request.linkedPOs ?? [];
    const startAt = request.startAt ? new Date(request.startAt) : null;
    if (!startAt || linkedPOs.length === 0) return null;

    const latePOs: { poNumber: string; dueDate: string; priority: string }[] = [];
    for (const po of linkedPOs) {
        if (po.dueDate && new Date(po.dueDate) < startAt) {
            latePOs.push({
                poNumber: po.poNumber,
                dueDate: po.dueDate,
                priority: po.priority || 'standard',
            });
        }
    }
    if (latePOs.length === 0) return null;

    const hasCritical = latePOs.some(p => p.priority === 'critical');
    const hasUrgent = latePOs.some(p => p.priority === 'urgent');
    const severity = hasCritical ? 'critical' : hasUrgent ? 'urgent' : 'standard';
    const label = hasCritical ? 'CRITICAL LATE RISK' : hasUrgent ? 'URGENT LATE RISK' : 'LATE PO RISK';

    return { severity, label, latePOs };
}

/** Numeric risk score for sorting (higher = more urgent) */
export function lateRiskSortScore(request: Appointment): number {
    const risk = detectLatePORisk(request);
    if (!risk) return 0;
    if (risk.severity === 'critical') return 30;
    if (risk.severity === 'urgent') return 20;
    return 10;
}

export function ApprovalQueueCard({
    request,
    onCardClick,
    onApprove,
    onRevise,
    onDecline,
    warehouseTimeZone = "America/Los_Angeles",
}: ApprovalQueueCardProps) {
    // ── Derived display values ──────────────────────
    const linkedPOs = request.linkedPOs ?? [];
    const totalPallets =
        linkedPOs.reduce((s, po) => s + (po.expectedPallets ?? 0), 0) +
        (linkedPOs.length === 0 && request.estimatedPalletCount ? request.estimatedPalletCount : 0);
    const hasDock = !!(request.dockId || request.dock_id);
    const dockLabel = request.dockNumber ? `Dock ${request.dockNumber}` : null;
    const scheduledMs = request.startAt ? new Date(request.startAt).getTime() : NaN;
    const isArrivingSoon =
        !isNaN(scheduledMs) && scheduledMs > Date.now() && scheduledMs <= Date.now() + 60 * 60 * 1000;
    const scheduledTime = !isNaN(scheduledMs)
        ? formatTimeInTimeZone(request.startAt, warehouseTimeZone)
        : 'N/A';
    const carrierDisplay = request.carrierName || 'Unknown Carrier';
    const mcDisplay = request.carrierMcNumber ? `MC ${request.carrierMcNumber}` : null;

    // ── Late PO Risk detection ──────────────────────
    const lateRisk = detectLatePORisk(request);

    const borderColor = lateRisk
        ? lateRisk.severity === 'critical'
            ? 'border-red-400 dark:border-red-600'
            : lateRisk.severity === 'urgent'
            ? 'border-orange-400 dark:border-orange-600'
            : 'border-amber-400 dark:border-amber-600'
        : 'border-slate-200 dark:border-slate-800';

    const hoverBorder = lateRisk
        ? lateRisk.severity === 'critical'
            ? 'hover:border-red-500 dark:hover:border-red-500'
            : lateRisk.severity === 'urgent'
            ? 'hover:border-orange-500 dark:hover:border-orange-500'
            : 'hover:border-amber-500 dark:hover:border-amber-500'
        : 'hover:border-amber-400 dark:hover:border-amber-600';

    const bgColor = lateRisk
        ? lateRisk.severity === 'critical'
            ? 'bg-red-50/60 dark:bg-red-950/20'
            : lateRisk.severity === 'urgent'
            ? 'bg-orange-50/60 dark:bg-orange-950/20'
            : 'bg-amber-50/60 dark:bg-amber-950/20'
        : 'bg-white dark:bg-slate-900';

    return (
        <div
            className={`rounded-lg border ${borderColor} ${bgColor} p-3.5 ${hoverBorder} transition-colors cursor-pointer`}
            onClick={onCardClick}
        >
            {/* Late PO Risk Banner */}
            {lateRisk && (
                <div className={`flex items-start gap-2 mb-3 pb-2.5 border-b ${
                    lateRisk.severity === 'critical' ? 'border-red-200 dark:border-red-800/50'
                    : lateRisk.severity === 'urgent' ? 'border-orange-200 dark:border-orange-800/50'
                    : 'border-amber-200 dark:border-amber-800/50'
                }`}>
                    <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${
                        lateRisk.severity === 'critical' ? 'text-red-500'
                        : lateRisk.severity === 'urgent' ? 'text-orange-500'
                        : 'text-amber-500'
                    }`} />
                    <div className="min-w-0">
                        <span className={`text-[11px] font-extrabold uppercase tracking-wider ${
                            lateRisk.severity === 'critical' ? 'text-red-600 dark:text-red-400'
                            : lateRisk.severity === 'urgent' ? 'text-orange-600 dark:text-orange-400'
                            : 'text-amber-600 dark:text-amber-400'
                        }`}>
                            {lateRisk.label}
                        </span>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                            {lateRisk.latePOs.map((po, i) => (
                                <span key={po.poNumber + i} className={`text-[10px] font-semibold ${
                                    lateRisk.severity === 'critical' ? 'text-red-500/80 dark:text-red-400/80'
                                    : lateRisk.severity === 'urgent' ? 'text-orange-500/80 dark:text-orange-400/80'
                                    : 'text-amber-600/80 dark:text-amber-400/80'
                                }`}>
                                    {po.poNumber} due {new Date(po.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}
                                    {' — Requested '}
                                    {formatDateInTimeZone(request.startAt, warehouseTimeZone)}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Row 1: Carrier + MC + Time */}
            <div className="flex items-baseline justify-between gap-2 mb-2">
                <div className="flex items-baseline gap-2 min-w-0">
                    <span className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate">
                        {carrierDisplay}
                    </span>
                    {mcDisplay && (
                        <span className="text-[11px] text-slate-400 dark:text-slate-500 font-mono shrink-0">
                            {mcDisplay}
                        </span>
                    )}
                </div>
                <span className="text-xs text-slate-700 dark:text-slate-300 font-semibold tabular-nums shrink-0">
                    {scheduledTime}
                </span>
            </div>

            {/* Row 2: PO chips + dock + pallet count */}
            <div className="flex items-center gap-1.5 flex-wrap mb-3">
                {linkedPOs.length > 0 ? (
                    <>
                        {linkedPOs.slice(0, 3).map((po, i) => (
                            <span
                                key={po.poNumber + i}
                                className="inline-flex items-center text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700"
                            >
                                {po.poNumber}
                            </span>
                        ))}
                        {linkedPOs.length > 3 && (
                            <span className="text-[10px] text-slate-400 dark:text-slate-500">
                                +{linkedPOs.length - 3}
                            </span>
                        )}
                    </>
                ) : request.customerPoNumber ? (
                    <span className="inline-flex items-center text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
                        {request.customerPoNumber}
                    </span>
                ) : null}
                {request.loadNumber && (
                    <span className="inline-flex items-center text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-400">
                        {request.loadNumber}
                    </span>
                )}
                {hasDock && dockLabel ? (
                    <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
                        {dockLabel}
                    </span>
                ) : (
                    <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-slate-50 dark:bg-slate-800 text-slate-400 dark:text-slate-500">
                        No dock
                    </span>
                )}
                {totalPallets > 0 && (
                    <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-400 tabular-nums">
                        {totalPallets} plt
                    </span>
                )}
                {isArrivingSoon && (
                    <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400">
                        Arriving soon
                    </span>
                )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <button
                    onClick={onApprove}
                    className="flex items-center gap-1 px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-md transition-colors"
                >
                    Approve
                </button>
                <button
                    onClick={onRevise}
                    className="flex items-center gap-1 px-3 py-1.5 border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs font-semibold rounded-md transition-colors"
                >
                    Revise
                </button>
                <button
                    onClick={onDecline}
                    className="flex items-center gap-1 px-3 py-1.5 text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 text-xs font-medium rounded-md transition-colors"
                >
                    Decline
                </button>
            </div>
        </div>
    );
}
